import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Matter,
  Sponsor,
  MatterHistory,
  MatterAttachment,
} from '../../types/legislation';
import {
  fetchMatter,
  fetchMatterSponsors,
  fetchMatterHistory,
  fetchMatterAttachments,
  getAttachmentFileUrl,
} from '../../services/legislationApi';
import rtfToText from '../../utils/rtfToText';

const LegislationDetail: React.FC = () => {
  const { matterId } = useParams<{ matterId: string }>();
  const [matter, setMatter] = useState<Matter | null>(null);
  const [sponsors, setSponsors] = useState<Sponsor[]>([]);
  const [history, setHistory] = useState<MatterHistory[]>([]);
  const [attachments, setAttachments] = useState<MatterAttachment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadMatterDetails = async () => {
      if (!matterId) return;

      try {
        setLoading(true);
        setError(null);

        const matterIdNum = parseInt(matterId, 10);

        // Fetch all data in parallel
        const [matterData, sponsorsData, historyData, attachmentsData] = await Promise.all([
          fetchMatter(matterIdNum),
          fetchMatterSponsors(matterIdNum).catch(() => []),
          fetchMatterHistory(matterIdNum).catch(() => []),
          fetchMatterAttachments(matterIdNum).catch(() => []),
        ]);

        setMatter(matterData);
        setSponsors(sponsorsData);
        setHistory(historyData);
        setAttachments(attachmentsData);
      } catch (err) {
        setError('Failed to load legislation details. Please try again.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    loadMatterDetails();
  }, [matterId]);

  const formatDate = (dateString: string | null): string => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const getStatusColor = (status: string): string => {
    const statusLower = status.toLowerCase();
    if (statusLower.includes('enacted') || statusLower.includes('adopted')) {
      return 'bg-green-100 text-green-800 border-green-200';
    }
    if (statusLower.includes('introduced')) {
      return 'bg-blue-100 text-blue-800 border-blue-200';
    }
    if (statusLower.includes('committee')) {
      return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    }
    return 'bg-gray-100 text-gray-800 border-gray-200';
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-nyc-blue mx-auto"></div>
        <p className="mt-4 text-gray-600">Loading legislation details...</p>
      </div>
    );
  }

  if (error || !matter) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">{error || 'Legislation not found'}</p>
        <Link to="/legislation" className="mt-4 inline-block text-nyc-blue hover:underline">
          &larr; Back to search
        </Link>
      </div>
    );
  }

  // Separate testimony documents from other attachments
  const testimonyDocs = attachments.filter((att) => {
    const name = (att.MatterAttachmentName || att.MatterAttachmentFileName || '').toLowerCase();
    return name.includes('testimony') || name.includes('statement') || name.includes('comment');
  });

  const otherDocs = attachments.filter((att) => {
    const name = (att.MatterAttachmentName || att.MatterAttachmentFileName || '').toLowerCase();
    return !name.includes('testimony') && !name.includes('statement') && !name.includes('comment');
  });

  return (
    <div>
      {/* Back link */}
      <Link to="/legislation" className="inline-flex items-center text-nyc-blue hover:underline mb-4">
        &larr; Back to search
      </Link>

      <div className="bg-white rounded-lg shadow-md p-6">
        {/* Header */}
        <div className="mb-6">
          <div className="flex flex-wrap items-start justify-between gap-4 mb-2">
            <h1 className="text-2xl font-bold text-nyc-blue">{matter.MatterFile}</h1>
            <div className="flex flex-wrap gap-2">
              {matter.MatterBodyName && (
                <span className="px-3 py-1 text-sm rounded-full border bg-purple-100 text-purple-800 border-purple-200">
                  {matter.MatterBodyName}
                </span>
              )}
              <span className="px-3 py-1 text-sm rounded-full border bg-gray-100 text-gray-800">
                {matter.MatterTypeName}
              </span>
              <span className={`px-3 py-1 text-sm rounded-full border ${getStatusColor(matter.MatterStatusName)}`}>
                {matter.MatterStatusName}
              </span>
            </div>
          </div>
          <h2 className="text-xl text-gray-900">{matter.MatterTitle || matter.MatterName}</h2>
        </div>

        {/* Key Dates */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6 p-4 bg-gray-50 rounded-lg">
          <div>
            <p className="text-sm font-semibold text-gray-600">Introduced</p>
            <p className="text-gray-900">{formatDate(matter.MatterIntroDate)}</p>
          </div>
          {matter.CouncilVoteDate && (
            <div>
              <p className="text-sm font-semibold text-gray-600">Passed by Council</p>
              <p className="text-gray-900">{formatDate(matter.CouncilVoteDate)}</p>
            </div>
          )}
          {matter.EnactmentDate && (
            <div>
              <p className="text-sm font-semibold text-gray-600">{matter.EnactmentLabel || 'Enacted'}</p>
              <p className="text-gray-900">{formatDate(matter.EnactmentDate)}</p>
            </div>
          )}
          {matter.MatterEnactmentNumber && (
            <div>
              <p className="text-sm font-semibold text-gray-600">Local Law #</p>
              <p className="text-gray-900">{matter.MatterEnactmentNumber}</p>
            </div>
          )}
        </div>

        {/* Sponsors */}
        {sponsors.length > 0 && (
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Sponsors</h3>
            <div className="space-y-2">
              {sponsors
                .sort((a, b) => a.MatterSponsorSequence - b.MatterSponsorSequence)
                .map((sponsor, index) => (
                  <div key={sponsor.MatterSponsorId} className="flex items-center gap-2">
                    <span
                      className={`px-2 py-0.5 text-xs rounded ${
                        index === 0 ? 'bg-nyc-blue text-white' : 'bg-gray-200 text-gray-700'
                      }`}
                    >
                      {index === 0 ? 'Primary' : 'Co-sponsor'}
                    </span>
                    <span className="text-gray-900">{sponsor.MatterSponsorName}</span>
                  </div>
                ))}
            </div>
          </div>
        )}

        {/* Legislative History */}
        {history.length > 0 && (
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Legislative History</h3>
            <div className="space-y-3">
              {history
                .sort((a, b) => new Date(b.MatterHistoryActionDate).getTime() - new Date(a.MatterHistoryActionDate).getTime())
                .map((item) => {
                  const isReferral = item.MatterHistoryActionName?.toLowerCase().includes('referred');
                  const isHearing = item.MatterHistoryActionName?.toLowerCase().includes('hearing');
                  const isPassed = item.MatterHistoryPassedFlag === 1;

                  return (
                    <div
                      key={item.MatterHistoryId}
                      className={`border-l-4 pl-4 py-2 ${
                        isPassed
                          ? 'border-green-500 bg-green-50'
                          : isHearing
                          ? 'border-nyc-blue bg-blue-50'
                          : isReferral
                          ? 'border-nyc-orange bg-orange-50'
                          : 'border-gray-300'
                      }`}
                    >
                      <div className="text-sm text-gray-500">
                        {formatDate(item.MatterHistoryActionDate)}
                      </div>
                      <div className="font-medium text-gray-900">{item.MatterHistoryActionName}</div>
                      {item.MatterHistoryActionBodyName && (
                        <div className="text-sm text-gray-600">{item.MatterHistoryActionBodyName}</div>
                      )}
                      {item.MatterHistoryActionText && (
                        <div className="text-sm text-gray-600 mt-1">
                          {rtfToText(item.MatterHistoryActionText)}
                        </div>
                      )}
                    </div>
                  );
                })}
            </div>
          </div>
        )}

        {/* Testimony Section */}
        {testimonyDocs.length > 0 && (
          <div className="mb-6 p-4 bg-green-50 rounded-lg">
            <h3 className="text-lg font-semibold text-green-800 mb-3">Testimony & Public Comments</h3>
            <div className="space-y-2">
              {testimonyDocs.map((attachment) => {
                const fileNameLower = attachment.MatterAttachmentFileName?.toLowerCase() || '';
                const isViewable = fileNameLower.endsWith('.pdf') || fileNameLower.endsWith('.docx');
                const docName = encodeURIComponent(
                  attachment.MatterAttachmentFileName || attachment.MatterAttachmentName || 'Document'
                );

                return (
                  <div key={attachment.MatterAttachmentId}>
                    {isViewable ? (
                      <Link
                        to={`/legislation/${matterId}/document/${attachment.MatterAttachmentId}/${docName}`}
                        className="text-green-700 hover:underline inline-flex items-center gap-1"
                      >
                        <span>📄</span>
                        {attachment.MatterAttachmentName || attachment.MatterAttachmentFileName}
                      </Link>
                    ) : (
                      <a
                        href={getAttachmentFileUrl(parseInt(matterId!, 10), attachment.MatterAttachmentId)}
                        className="text-green-700 hover:underline inline-flex items-center gap-1"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <span>📄</span>
                        {attachment.MatterAttachmentName || attachment.MatterAttachmentFileName}
                      </a>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Other Attachments */}
        {otherDocs.length > 0 && (
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Documents & Attachments</h3>
            <div className="space-y-2">
              {otherDocs.map((attachment) => {
                const fileNameLower = attachment.MatterAttachmentFileName?.toLowerCase() || '';
                const isViewable = fileNameLower.endsWith('.pdf') || fileNameLower.endsWith('.docx');
                const docName = encodeURIComponent(
                  attachment.MatterAttachmentFileName || attachment.MatterAttachmentName || 'Document'
                );

                return (
                  <div key={attachment.MatterAttachmentId}>
                    {isViewable ? (
                      <Link
                        to={`/legislation/${matterId}/document/${attachment.MatterAttachmentId}/${docName}`}
                        className="text-nyc-orange hover:underline inline-flex items-center gap-1"
                      >
                        <span>📄</span>
                        {attachment.MatterAttachmentName || attachment.MatterAttachmentFileName}
                      </Link>
                    ) : (
                      <a
                        href={getAttachmentFileUrl(parseInt(matterId!, 10), attachment.MatterAttachmentId)}
                        className="text-nyc-orange hover:underline inline-flex items-center gap-1"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <span>📄</span>
                        {attachment.MatterAttachmentName || attachment.MatterAttachmentFileName}
                      </a>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* External Link */}
        <div className="pt-4 border-t border-gray-200">
          <a
            href={`https://legistar.council.nyc.gov/LegislationDetail.aspx?ID=${matter.MatterId}&GUID=${matter.MatterGuid}&Options=&Search=`}
            className="text-nyc-blue hover:underline inline-flex items-center gap-1"
            target="_blank"
            rel="noopener noreferrer"
          >
            View on NYC Council Legistar
            <span>&rarr;</span>
          </a>
        </div>
      </div>
    </div>
  );
};

export default LegislationDetail;
